import { withZod } from '@remix-validated-form/with-zod'
import { ArrowRight, WarningHexagon } from 'iconoir-react'
import { startCase } from 'lodash'
import { useMemo, useState } from 'react'
import { z } from 'zod'
import { useMutation } from 'urql'
import { graphql } from 'app/generated/gql'
import Button from '../Button'
import { useConfirmation } from '../ConfirmationContext'
import FormInput from '../form/FormInput'
import { SmartForm, SubAction } from '../form/SmartForm'
import { UtmAlias, UtmAliasJson } from 'app/types/entities'
import { closeModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useWorkspace } from 'app/utils'
import { logError } from 'lib/logging'

const sourceAliasValidator = (type) =>
  withZod(
    z
      .object({
        original: z.string().min(1, { message: `${startCase(type)} is required` }),
        alias: z.string().min(1, { message: `${startCase(type)} is required` }),
        type: z.literal(`source`).or(z.literal(`medium`)),
      })
      .superRefine(({ original, alias }, ctx) => {
        if (original === alias) {
          ctx.addIssue({
            code: `custom`,
            message: `Original and Updated cannot be the same`,
            path: [`alias`],
          })
        }
      }),
  )

const CREATE_UTM_ALIAS = graphql(`
  mutation CreateUtmAlias($input: CreateUtmAliasInput!) {
    createUtmAlias(input: $input) {
      id
      original
      alias
      type
    }
  }
`)

const UPDATE_UTM_ALIAS = graphql(`
  mutation UpdateUtmAlias($id: ID!, $input: UpdateUtmAliasInput!) {
    updateUtmAlias(id: $id, input: $input) {
      id
      original
      alias
      type
    }
  }
`)

const DELETE_UTM_ALIAS = graphql(`
  mutation DeleteUtmAlias($id: ID!) {
    deleteUtmAlias(id: $id)
  }
`)

export function AliasForm({ utmAlias, type }: { utmAlias?: UtmAliasJson; type: UtmAlias[`type`] }) {
  const [confirm] = useConfirmation()
  const workspace = useWorkspace()
  const [createResult, executeCreate] = useMutation(CREATE_UTM_ALIAS)
  const [updateResult, executeUpdate] = useMutation(UPDATE_UTM_ALIAS)
  const [deleteResult, executeDelete] = useMutation(DELETE_UTM_ALIAS)
  const isLoading = createResult.fetching || updateResult.fetching || deleteResult.fetching
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false)
  const subAction = useMemo<SubAction>(() => (utmAlias?.id ? SubAction.EDIT : SubAction.CREATE), [utmAlias])
  const onSubmit = async (data: Partial<UtmAlias>, subAction: SubAction): Promise<void> => {
    setIsDuplicate(false)
    const result = await sourceAliasValidator(type).validate(data)
    if (workspace == null) {
      logError(`workspace not found`)
      notifyError(`Please select a workspace`)
      return
    }
    if (result.error) {
      logError(`error on ${type} alias data`, result.error)
      notifyError(`Data Error`)
      return
    }
    if (subAction === SubAction.CREATE) {
      const { error } = await executeCreate({
        input: { original: result.data.original, alias: result.data.alias, type: result.data.type },
      })
      if (error) {
        setIsDuplicate(true)
        return
      }
      notifySuccess(`${startCase(type)} Alias Created`)
      closeModal()
    } else if (subAction === SubAction.EDIT) {
      const { error } = await executeUpdate({
        id: utmAlias!.id!,
        input: { original: result.data.original, alias: result.data.alias, type: result.data.type },
      })
      if (error) {
        setIsDuplicate(true)
        return
      }
      notifySuccess(`Change Saved. This may take some time to fully apply.`)
      closeModal()
    }
  }

  const handleDelete = async (id: string) => {
    await confirm({
      title: `Delete Channel Mapping`,
      description: `Are you sure you want to delete this alias?`,
      variant: `danger`,
    }).then(async () => {
      const { error } = await executeDelete({ id }, { additionalTypenames: ['UtmAlias'] })
      if (error) {
        logError(`Error deleting medium`, error)
        notifyError(`Error deleting medium`)
        return
      }
      notifySuccess(`Alias deleted`)
      closeModal()
    })
  }

  return (
    <SmartForm<Partial<UtmAlias>>
      id={`${type}-alias-form`}
      validator={sourceAliasValidator(type)}
      className="mb-0 mt-0 flex w-full flex-col gap-y-0"
      onSubmit={(data) => onSubmit(data, subAction)}
      defaultValues={utmAlias}
      hiddenInputs={[{ name: `type`, value: type }]}
      method={utmAlias?.id ? `PUT` : `POST`}
      hideButtons
      preventSubmit
    >
      <div className="col-span-6 flex flex-col px-6 py-4">
        <div className="flex items-start gap-x-2">
          <FormInput
            labelClassName="text-strong text-body-sm font-medium"
            className="h-10 !w-64 !px-2 !py-1"
            name="original"
            type="text"
            label={`Original ${startCase(type)}`}
            cols={3}
          />
          <ArrowRight className="size-5 mt-9" strokeWidth={1.5} />
          <FormInput
            labelClassName="text-strong text-body-sm font-medium"
            className="h-10 !w-64 !px-2 !py-1"
            name="alias"
            type="text"
            label={`Updated ${startCase(type)}`}
            cols={3}
          />
        </div>
      </div>
      <div className="border-t-trn col-span-6 flex w-full items-center justify-between border px-6 py-3">
        <Button
          mode="danger"
          size="sm"
          label="Delete"
          className={`${subAction === SubAction.EDIT ? `` : `invisible`}`}
          onClick={async () => handleDelete(utmAlias?.id!)}
          loading={isLoading}
        />
        <div
          className={`${
            isDuplicate ? `inline-flex` : `invisible`
          } items-center justify-center gap-x-2 text-center text-red-700`}
        >
          <WarningHexagon className="size-4" />
          <span className="text-body-xs">{`This ${startCase(type)} Already Exists`}</span>
        </div>
        {subAction === SubAction.CREATE && (
          <Button mode="primary" size="sm" label="Create" type="submit" className="text-center" loading={isLoading} />
        )}
        {subAction === SubAction.EDIT && (
          <Button mode="primary" size="sm" label="Update" type="submit" className="text-center" loading={isLoading} />
        )}
      </div>
    </SmartForm>
  )
}
